import { Box, Collapse, Radio, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { FC, useEffect, useState, memo, useCallback, useMemo } from 'react';
import { ArrowDown, ArrowUp, Minus, Plus } from '../../../assets';
import UoMDetailsTable from './UoMDetailsTable';
import { useTranslation } from 'react-i18next';
import { TempCategoryItem, TempStockCheckItem } from '..';
import { applyRoundAndFormat } from '../../../utils';
import React from 'react';

type Props = {
    category: TempCategoryItem;
    categoryIndex: number;
    zoneIndex?: number;
    onItemStatusChange?: (itemId: number | undefined, checked: boolean) => void;
    sortPartialStockCountItems: (fieldToSort: keyof TempStockCheckItem, sortDirection: 'asc' | 'desc') => void;
    togglePartialStockCountItem: (itemNumber: string | undefined) => void;
    isDisabled: boolean;
    isViewMode?: boolean;
    isOpenedInDialog?: boolean;
    bulkApprovalState?: boolean | null;
    currencyCode: string;
};

const StockCheckCategoryTable: FC<Props> = memo(({
  category,
  onItemStatusChange,
  sortPartialStockCountItems,
  togglePartialStockCountItem,
  isDisabled,
  isViewMode,
  isOpenedInDialog,
  bulkApprovalState,
  currencyCode,
}) => {
  const { t } = useTranslation('stockCountReview');

  const initialSortByList = useMemo(() => [
    { id: 'itemNumber', name: 'ItemNumber', is_active: false },
    { id: 'description', name: 'ItemNumber', is_active: false },
    { id: 'locationString', name: 'LocationString', is_active: false },
    { id: 'primaryUom', name: 'PrimaryUOM', is_active: false },
    { id: 'quantityOnHand', name: 'QuantityOnHand', is_active: false },
    { id: 'countQuantityTotal', name: 'CountQuantity', is_active: false },
    { id: 'varianceQuantity', name: 'VarianceQuantity', is_active: false },
    { id: 'variancePercentage', name: 'VariancePercentage', is_active: false },
    { id: 'costPriceOnHand', name: 'CostPriceOnHand', is_active: false },
    { id: 'totalCostPrice', name: 'TotalCostPrice', is_active: false },
    { id: 'varianceValue', name: 'VarianceValue', is_active: false },
  ], []);

  const [sortByList, setSortByList] = useState(initialSortByList);
  const [sortedItems, setSortedItems] = useState<TempStockCheckItem[]>(
    category.name === ''
      ? [...category.items].sort((a, b) => {
        if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
          return a.rowNumber - b.rowNumber;
        }
        if (a.rowNumber !== undefined) return -1;
        if (b.rowNumber !== undefined) return 1;
        return (a.description || '').localeCompare(b.description || '');
      })
      : [...category.items].sort((a, b) => (a.description || '').localeCompare(b.description || '')),
  );

  useEffect(() => {
    setSortedItems(
      category.name === ''
        ? [...category.items].sort((a, b) => {
          if (a.rowNumber !== undefined && b.rowNumber !== undefined) {
            return a.rowNumber - b.rowNumber;
          }
          if (a.rowNumber !== undefined) return -1;
          if (b.rowNumber !== undefined) return 1;
          return (a.description || '').localeCompare(b.description || '');
        })
        : [...category.items].sort((a, b) => (a.description || '').localeCompare(b.description || '')),
    );
    setSortByList(initialSortByList);
  }, [category.items, category.name, initialSortByList]);

  const togglePartialOrderBy = useCallback((column: string) => {
    setSortByList(prevSortByList => {
      let sortDirection: 'asc' | 'desc' = 'asc';

      const updatedSortByList = prevSortByList.map((sortItem) => {
        if (sortItem.id === column) {
          sortItem.is_active = !sortItem.is_active;
          sortDirection = sortItem.is_active ? 'asc' : 'desc';
        } else {
          sortItem.is_active = false;
        }
        return sortItem;
      });

      setSortedItems(prevItems => {
        const newSortedItems = [...prevItems];
        const sortField = column === 'totalCostPrice' ? 'totalCostPrice' : column;

        if (sortField === 'totalCostPrice') {
          const direction = sortDirection === 'asc' ? 1 : -1;
          newSortedItems.sort((a, b) => {
            const aValue = (a.countQuantityTotal ?? 0) * (a.costPriceOnHand ?? 0);
            const bValue = (b.countQuantityTotal ?? 0) * (b.costPriceOnHand ?? 0);
            return (aValue - bValue) * direction;
          });
        } else {
          newSortedItems.sort((a, b) => {
            const aVal = a[sortField as keyof TempStockCheckItem];
            const bVal = b[sortField as keyof TempStockCheckItem];
            if (typeof aVal === 'string' && typeof bVal === 'string') {
              return sortDirection === 'asc'
                ? (aVal || '').localeCompare(bVal || '')
                : (bVal || '').localeCompare(aVal || '');
            }
            return sortDirection === 'asc'
              ? Number(aVal) - Number(bVal)
              : Number(bVal) - Number(aVal);
          });
        }
        return newSortedItems;
      });

      return updatedSortByList;
    });
  }, []);

  const getVariancePercentage = useCallback((item: TempStockCheckItem) => {
    if ((item.countQuantityTotal === 0 && (item.quantityOnHand ?? 0) > 0) || ((item.quantityOnHand ?? 0) < 0 && item.countQuantityTotal === 0)) return -100.00;
    if ((item.countQuantityTotal ?? 0) > 0 && item.quantityOnHand === 0) return 100.00;
    if (item.countQuantityTotal === 0 && item.quantityOnHand === 0) return 0;
  }, []);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <StockCheckCellHeader
              label='SKU Number'
              onClick={() => togglePartialOrderBy('itemNumber')}
              isActive={sortByList[0].is_active}
            />
            <StockCheckCellHeader
              label='SKU Description'
              onClick={() => togglePartialOrderBy('description')}
              isActive={sortByList[1].is_active}
            />
            <StockCheckCellHeader
              label='UoM'
              onClick={() => togglePartialOrderBy('primaryUom')}
              isActive={sortByList[3].is_active}
            />
            <StockCheckCellHeader
              label='System Qty'
              onClick={() => togglePartialOrderBy('quantityOnHand')}
              isActive={sortByList[4].is_active}
            />
            <StockCheckCellHeader
              label='Count Qty'
              onClick={() => togglePartialOrderBy('countQuantityTotal')}
              isActive={sortByList[5].is_active}
            />
            <StockCheckCellHeader
              label='Variance Qty'
              onClick={() => togglePartialOrderBy('varianceQuantity')}
              isActive={sortByList[6].is_active}
            />
            <StockCheckCellHeader
              label='%'
              onClick={() => togglePartialOrderBy('variancePercentage')}
              isActive={sortByList[7].is_active}
            />
            <StockCheckCellHeader
              label='UoM Cost'
              onClick={() => togglePartialOrderBy('costPriceOnHand')}
              isActive={sortByList[8].is_active}
            />
            <StockCheckCellHeader
              label='Count Value'
              onClick={() => togglePartialOrderBy('totalCostPrice')}
              isActive={sortByList[9].is_active}
            />
            <StockCheckCellHeader
              label='Variance Value'
              onClick={() => togglePartialOrderBy('varianceValue')}
              isActive={sortByList[10].is_active}
            />
            {!isViewMode && <TableCell align="left">{t('approve')}</TableCell>}
            {!isViewMode && <TableCell align="left">{t('reject')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((row: TempStockCheckItem, i: number) => (
            <StockCheckTableRow
              key={`${row.id}`}
              row={row}
              index={i}
              currencyCode={currencyCode}
              isViewMode={isViewMode}
              isOpenedInDialog={isOpenedInDialog}
              isDisabled={isDisabled}
              onItemStatusChange={onItemStatusChange}
              togglePartialStockCountItem={togglePartialStockCountItem}
              getVariancePercentage={getVariancePercentage}
              bulkApprovalState={bulkApprovalState}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

StockCheckCategoryTable.displayName = 'StockCheckCategoryTable';

// Memoized row component
type StockCheckTableRowProps = {
    row: TempStockCheckItem;
    index: number;
    currencyCode: string;
    isViewMode?: boolean;
    isOpenedInDialog?: boolean;
    isDisabled: boolean;
    onItemStatusChange?: (itemId: number | undefined, checked: boolean) => void;
    togglePartialStockCountItem: (itemNumber: string | undefined) => void;
    getVariancePercentage: (item: TempStockCheckItem) => number | undefined;
    bulkApprovalState?: boolean | null;
};

const StockCheckTableRow = memo(({
  row,
  index,
  currencyCode,
  isViewMode,
  isOpenedInDialog,
  isDisabled,
  onItemStatusChange,
  togglePartialStockCountItem,
  getVariancePercentage,
  bulkApprovalState,
}: StockCheckTableRowProps) => {
  // Local state for optimistic UI update
  const [optimisticApprovalState, setOptimisticApprovalState] = React.useState<boolean | undefined>(row.isApproved);

  React.useEffect(() => {
    if (bulkApprovalState !== null && bulkApprovalState !== undefined) {
      // Bulk operation - update immediately
      setOptimisticApprovalState(bulkApprovalState);
    } else {
      // Normal operation - sync with prop
      setOptimisticApprovalState(row.isApproved);
    }
  }, [row.isApproved, bulkApprovalState]);

  const handleApprove = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setOptimisticApprovalState(true);
    onItemStatusChange?.(row.id, true);
  }, [onItemStatusChange, row.id]);

  const handleReject = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setOptimisticApprovalState(false);
    onItemStatusChange?.(row.id, false);
  }, [onItemStatusChange, row.id]);

  const handleToggleExpand = useCallback(() => {
    togglePartialStockCountItem(row.itemNumber);
  }, [togglePartialStockCountItem, row.itemNumber]);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <Box display='flex'
            alignItems='center'>
            {row.itemValues && row.itemValues.length > 1 && !isOpenedInDialog && (
              <SvgIcon onClick={handleToggleExpand}
                sx={{ cursor: 'pointer' }}>
                {row.isExpanded ? <Minus /> : <Plus />}
              </SvgIcon>
            )}
            {row.itemNumber}
          </Box>
        </TableCell>

        <StockCheckCellData value={row.description} />
        <StockCheckCellData value={row.primaryUom ?? '-'} />
        <StockCheckCellData value={row.quantityOnHand?.toFixed(2)}
          title={row.quantityOnHand?.toString()} />
        <StockCheckCellData value={row.countQuantityTotal ?? '-'}
          title={row.countQuantityTotal?.toString()} />
        <StockCheckCellData value={row.varianceQuantity?.toFixed(2) ?? '-'}
          title={row.varianceQuantity?.toString()} />
        <StockCheckCellData value={row.variancePercentage?.toFixed(2) ?? getVariancePercentage(row)}
          title={row.variancePercentage?.toString()} />
        <StockCheckCellData value={applyRoundAndFormat(row.costPriceOnHand ?? 0, currencyCode)}
          title={applyRoundAndFormat(row.costPriceOnHand ?? 0, currencyCode, 8)} />
        <StockCheckCellData value={applyRoundAndFormat(((row.countQuantityTotal ?? 0) * (row.costPriceOnHand ?? 0)), currencyCode)}
          title={applyRoundAndFormat(((row.countQuantityTotal ?? 0) * (row.costPriceOnHand ?? 0)), currencyCode, 8)} />
        <StockCheckCellData value={applyRoundAndFormat((row.varianceValue ?? 0), currencyCode)}
          title={applyRoundAndFormat((row.varianceValue ?? 0), currencyCode, 8)} />

        {!isViewMode && (
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Radio
              checked={optimisticApprovalState === true}
              onChange={handleApprove}
              value="true"
              name={`isApproved-${row.id}-${index}`}
              disabled={isDisabled}
            />
          </TableCell>
        )}
        {!isViewMode && (
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Radio
              checked={optimisticApprovalState === false}
              onChange={handleReject}
              value="false"
              name={`isApproved-${row.id}-${index}`}
              disabled={isDisabled}
            />
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell colSpan={isViewMode ? 10 : 12}
          sx={{ p: 0, m: 0, border: 0 }}>
          <Collapse in={row.isExpanded}>
            <UoMDetailsTable itemValues={row.itemValues} />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  if (prevProps.bulkApprovalState !== nextProps.bulkApprovalState) {
    return false;
  }

  // In dialog mode, always allow re-renders for approval changes
  if (nextProps.isOpenedInDialog) {
    return (
      prevProps.row.id === nextProps.row.id &&
            prevProps.row.isApproved === nextProps.row.isApproved &&
            prevProps.isDisabled === nextProps.isDisabled
    );
  }

  // Normal mode: full comparison
  return (
    prevProps.row.id === nextProps.row.id &&
        prevProps.row.isApproved === nextProps.row.isApproved &&
        prevProps.row.isExpanded === nextProps.row.isExpanded &&
        prevProps.isDisabled === nextProps.isDisabled &&
        prevProps.currencyCode === nextProps.currencyCode
  );
});

StockCheckTableRow.displayName = 'StockCheckTableRow';

type StockCheckCellHeaderProps = {
    label: string;
    onClick: () => void;
    isActive: boolean
}

const StockCheckCellHeader = ({ label, onClick, isActive }: StockCheckCellHeaderProps) => {
  const [isCurrentActive, setIsCurrentActive] = useState<boolean>(isActive);

  useEffect(() => {
    setIsCurrentActive(isActive);
  }, [isActive]);

  return (
    <TableCell
      sx={{ cursor: 'pointer' }}
      align="left"
      onClick={onClick}
    >
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
      >
        <Typography
          variant="textSM"
          fontWeight='bold'
        >
          {label}
        </Typography>
        <SvgIcon sx={{ width: '20px', pl: 1 }}>
          {isCurrentActive ? <ArrowUp /> : <ArrowDown />}
        </SvgIcon>
      </Box>
    </TableCell>
  );
};

type StockCheckCellDataProps = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    title?: string;
}

const StockCheckCellData = ({ value, title }: StockCheckCellDataProps) => {
  return (
    <TableCell title={title}>
      {value ?? ''}
    </TableCell>
  );
};

export default StockCheckCategoryTable;
